import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Container,
  IconButton
} from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import Api from '../../utils/api';

const PerfilEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const api = useMemo(() => new Api(), []);
  const auth = useSelector((state) => state.auth);

  const [hasError, setHasError] = useState(null); // string | null
  const [hasSuccess, setHasSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (values, actions) => {
    setHasError(null);
    setHasSuccess(null);
    setIsLoading(true);
    actions.setSubmitting(true);

    try {
      const param = {
        APELIDO: auth.user.APELIDO,
        SENHA: values.senhaAtual,
        NOVA_SENHA: values.senhaNova,
        grant_type: auth.perfil
      };

      // ajuste a rota se necessário
      const { data } = await api.put(`/login/troca-senha`, param, auth.token);

      if (!data || !data.status) {
        throw new Error(data.msg || 'Erro ao atualizar');
      }

      setHasSuccess('Senha atualizada com sucesso.');

      await swal({
        title: 'Sucesso',
        text: 'Senha atualizada com sucesso!',
        icon: 'success'
      });

      navigate(-1);
    } catch (e) {
      console.error(e);
      setHasError(e?.response?.data?.message || e.message || 'Erro ao atualizar');
    } finally {
      setIsLoading(false);
      actions.setSubmitting(false);
    }
  };

  // definição dinâmica dos campos
  const fieldDefs = useMemo(
    () => [
      { label: 'Senha Atual', name: 'senhaAtual', type: 'password', required: true },
      { label: 'Nova Senha', name: 'senhaNova', type: 'password', required: true },
      { label: 'Confirmar Nova Senha', name: 'senhaNovaConfirm', type: 'password', required: true }
    ],
    []
  );

  const initialValues = useMemo(() => {
    const entries = fieldDefs.map((f) => [f.name, '']);
    return Object.fromEntries(entries);
  }, [fieldDefs]);

  const validationSchema = useMemo(() => {
    const shape = {};
    fieldDefs.forEach((f) => {
      let schema = Yup.string();
      if (f.required) schema = schema.required('Obrigatório');
      if (f.name === 'senhaNova') schema = schema.min(6, 'Mínimo 6 caracteres');
      if (f.name === 'senhaNovaConfirm') {
        schema = schema.oneOf([Yup.ref('senhaNova')], 'As senhas não conferem');
      }
      shape[f.name] = schema;
    });
    return Yup.object().shape(shape);
  }, [fieldDefs]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit: handleSubmitForm,
        isSubmitting,
        touched,
        values,
        resetForm
      }) => (
        <>
          <Helmet>
            <title>{`Perfil | ${process.env.REACT_APP_TITLE}`}</title>
          </Helmet>
          <Box
            sx={{
              backgroundColor: 'background.default',
              minHeight: '100%',
              py: 3
            }}
          >
            <Container maxWidth={false}>
              <Card>
                <CardHeader
                  subheader="Atualizar dados do Perfil"
                  title={auth.user.USUARIO}
                  avatar={
                    <IconButton size="small" color="primary" onClick={() => navigate(-1)}>
                      <ArrowBackIcon />
                    </IconButton>
                  }
                />

                <Divider />

                {(hasError || (errors && errors.length > 0)) && (
                  <CardContent sx={{ p: 0 }}>
                    <Alert severity="error">
                      <AlertTitle>Atenção</AlertTitle>
                      {hasError}
                      {Object.values(errors)}
                    </Alert>
                  </CardContent>
                )}

                {hasSuccess && (
                  <CardContent sx={{ p: 0 }}>
                    <Alert severity="success">
                      <AlertTitle>Sucesso</AlertTitle>
                      {hasSuccess}
                    </Alert>
                  </CardContent>
                )}

                <CardContent>
                  <form onSubmit={handleSubmitForm} noValidate>
                    <Grid container spacing={2}>
                      {fieldDefs.map((item) => (
                        <Grid item xs={12} sm={12} md={12} key={item.name}>
                          <TextField
                            fullWidth
                            label={item.label}
                            name={item.name}
                            type={item.type}
                            value={values[item.name] || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(touched[item.name] && errors[item.name])}
                            helperText={touched[item.name] && errors[item.name]}
                            autoComplete={item.type === 'password' ? 'new-password' : 'off'}
                            disabled={isSubmitting || isLoading}
                          />
                        </Grid>
                      ))}

                      <Grid item xs={12} sm={12} md={12} sx={{ height: 20 }} />

                      <Grid item xs={12} sm={6} md={6}>
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={() => resetForm()}
                          fullWidth
                          disabled={isSubmitting || isLoading}
                        >
                          Limpar
                        </Button>
                      </Grid>

                      <Grid item xs={12} sm={6} md={6}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={isSubmitting || isLoading}
                        >
                          {isSubmitting || isLoading ? (
                            <CircularProgress size={18} style={{ marginLeft: 8 }} />
                          ) : (
                            'Salvar'
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </Container>
          </Box>
        </>
      )}
    </Formik>
  );
};

export default PerfilEdit;
